import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/admin/DashboardLayout';
import RequireAdmin from '../components/admin/RequireAdmin';
import { Plus, Edit, Trash2, Power, Play, SwitchCamera, MessageSquare, ShieldAlert, CheckCircle2, AlertCircle } from 'lucide-react';

interface Webhook {
    id: string;
    name: string;
    url: string;
    is_active: boolean;
    on_user_activity: boolean;
    on_admin_activity: boolean;
}

export default function WebhooksPage() {
    const [webhooks, setWebhooks] = useState<Webhook[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Form State
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        url: '',
        is_active: true,
        on_user_activity: false,
        on_admin_activity: false
    });

    useEffect(() => {
        fetchWebhooks();
    }, []);

    const fetchWebhooks = async () => {
        try {
            const { data, error: sbError } = await supabase
                .from('system_webhooks')
                .select('*')
                .order('created_at', { ascending: false });

            if (sbError) throw sbError;
            setWebhooks(data || []);
        } catch (err: any) {
            console.error('Error fetching webhooks:', err);
            setError(err.message || 'Gagal memuat daftar webhook');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenForm = (webhook?: Webhook) => {
        if (webhook) {
            setFormData({
                name: webhook.name,
                url: webhook.url,
                is_active: webhook.is_active,
                on_user_activity: webhook.on_user_activity,
                on_admin_activity: webhook.on_admin_activity
            });
            setEditingId(webhook.id);
        } else {
            setFormData({
                name: '',
                url: '',
                is_active: true,
                on_user_activity: true,
                on_admin_activity: false
            });
            setEditingId(null);
        }
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingId(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);

        try {
            if (editingId) {
                const { error: sbError } = await supabase
                    .from('system_webhooks')
                    .update(formData)
                    .eq('id', editingId);
                if (sbError) throw sbError;
            } else {
                const { error: sbError } = await supabase
                    .from('system_webhooks')
                    .insert([formData]);
                if (sbError) throw sbError;
            }
            await fetchWebhooks();
            handleCloseForm();
        } catch (err: any) {
            console.error('Error saving webhook:', err);
            setError(err.message || 'Gagal menyimpan webhook');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Apakah Anda yakin ingin menghapus webhook ini?')) return;
        try {
            const { error: sbError } = await supabase
                .from('system_webhooks')
                .delete()
                .eq('id', id);
            if (sbError) throw sbError;
            await fetchWebhooks();
        } catch (err: any) {
            console.error('Error deleting webhook:', err);
            setError('Gagal menghapus webhook');
        }
    };

    const handleToggleActive = async (webhook: Webhook) => {
        try {
            const { error: sbError } = await supabase
                .from('system_webhooks')
                .update({ is_active: !webhook.is_active })
                .eq('id', webhook.id);
            if (sbError) throw sbError;
            await fetchWebhooks();
        } catch (err: any) {
            console.error('Error toggling webhook:', err);
            setError('Gagal mengubah status webhook');
        }
    };

    const handleTestWebhook = async (webhook: Webhook) => {
        setIsTesting(webhook.id);
        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData.session?.access_token;

            const response = await fetch('/api/ping-discord', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ webhookUrl: webhook.url, name: webhook.name })
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || `Server returned ${response.status}`);
            }

            alert('Test message sent successfully! Check your Discord channel.');
        } catch (err: any) {
            console.error('Test webhook failed:', err);
            alert(`Failed to send test message: ${err.message}`);
        } finally {
            setIsTesting(null);
        }
    };

    return (
        <RequireAdmin>
            <DashboardLayout>
                <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
                    <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Discord Webhooks</h1>
                            <p className="text-slate-400">Manage real-time Discord notifications for user and admin activities.</p>
                        </div>
                        <button
                            onClick={() => handleOpenForm()}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 font-medium w-full sm:w-auto justify-center"
                        >
                            <Plus size={20} />
                            <span>Integrasi Baru</span>
                        </button>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-red-900/40 border border-red-500/50 rounded-lg text-red-200 flex items-start gap-3 flex-wrap">
                            <AlertCircle size={20} className="shrink-0 mt-0.5" />
                            <span className="flex-1 break-words">{error}</span>
                        </div>
                    )}

                    {/* List Webhooks */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {isLoading ? (
                            <div className="col-span-full py-12 text-center text-slate-400">Loading webhooks...</div>
                        ) : webhooks.length === 0 ? (
                            <div className="col-span-full py-12 text-center bg-slate-800/50 rounded-xl border border-slate-700">
                                <MessageSquare className="mx-auto h-12 w-12 text-slate-500 mb-3" />
                                <h3 className="text-lg font-medium text-slate-300">Belum ada Webhook</h3>
                                <p className="text-slate-400 mt-1">Tambahkan webhook Discord untuk mulai menerima notifikasi.</p>
                            </div>
                        ) : (
                            webhooks.map((webhook) => (
                                <div key={webhook.id} className={`bg-slate-800 rounded-xl border transition-colors ${webhook.is_active ? 'border-slate-600 shadow-lg shadow-black/20' : 'border-slate-700 opacity-75'}`}>
                                    <div className="p-5 border-b border-slate-700 flex justify-between items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-semibold truncate" title={webhook.name}>
                                                {webhook.name}
                                            </h3>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${webhook.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-500/10 text-slate-400'}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${webhook.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                                                    {webhook.is_active ? 'Active' : 'Disabled'}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleToggleActive(webhook)}
                                            className={`p-2 rounded-lg transition-colors shrink-0 ${webhook.is_active ? 'text-emerald-400 hover:bg-emerald-400/10' : 'text-slate-500 hover:bg-slate-700'}`}
                                            title={webhook.is_active ? 'Disable' : 'Enable'}
                                        >
                                            <Power size={20} />
                                        </button>
                                    </div>

                                    <div className="p-5 space-y-4">
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Triggers Events</p>
                                            <div className="flex flex-wrap gap-2">
                                                {webhook.on_user_activity && (
                                                    <span className="inline-flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs px-2.5 py-1 rounded-md">
                                                        <SwitchCamera size={14} />
                                                        User Activity
                                                    </span>
                                                )}
                                                {webhook.on_admin_activity && (
                                                    <span className="inline-flex items-center gap-1.5 bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs px-2.5 py-1 rounded-md">
                                                        <ShieldAlert size={14} />
                                                        Admin Logs
                                                    </span>
                                                )}
                                                {!webhook.on_user_activity && !webhook.on_admin_activity && (
                                                    <span className="text-xs text-slate-500 italic">No events selected</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="pt-4 border-t border-slate-700 flex justify-between items-center bg-slate-800/50 -mx-5 -mb-5 p-5 rounded-b-xl">
                                            <button
                                                onClick={() => handleTestWebhook(webhook)}
                                                disabled={isTesting === webhook.id}
                                                className="text-sm flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors"
                                            >
                                                {isTesting === webhook.id ? (
                                                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <Play size={16} />
                                                )}
                                                Test Message
                                            </button>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => handleOpenForm(webhook)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                                                    <Edit size={16} />
                                                </button>
                                                <button onClick={() => handleDelete(webhook.id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Modal Form */}
                    {isFormOpen && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                            <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                <div className="p-6 border-b border-slate-700">
                                    <h2 className="text-xl font-bold">{editingId ? 'Edit Webhook' : 'Webhook Baru'}</h2>
                                </div>

                                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-1">
                                            Nama Integrasi
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="e.g., Main Logging Server"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-1">
                                            Discord Webhook URL
                                        </label>
                                        <input
                                            type="url"
                                            required
                                            value={formData.url}
                                            onChange={e => setFormData({ ...formData, url: e.target.value })}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                                            placeholder="https://discord.com/api/webhooks/..."
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <label className="block text-sm font-medium text-slate-300 mb-3">
                                            Events to Trigger
                                        </label>
                                        <div className="space-y-3">
                                            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/50 cursor-pointer hover:bg-slate-800 transition-colors">
                                                <div className="flex h-6 items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.on_user_activity}
                                                        onChange={e => setFormData({ ...formData, on_user_activity: e.target.checked })}
                                                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-slate-900"
                                                    />
                                                </div>
                                                <div className="text-sm">
                                                    <div className="font-medium text-white">User Activity</div>
                                                    <div className="text-slate-400">Tryout starts, profile views, WhatsApp clicks.</div>
                                                </div>
                                            </label>

                                            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/50 cursor-pointer hover:bg-slate-800 transition-colors">
                                                <div className="flex h-6 items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.on_admin_activity}
                                                        onChange={e => setFormData({ ...formData, on_admin_activity: e.target.checked })}
                                                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-slate-900"
                                                    />
                                                </div>
                                                <div className="text-sm">
                                                    <div className="font-medium text-white">Admin Activity <span className="ml-2 text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Sensitive</span></div>
                                                    <div className="text-slate-400">Logins, data deletion, bulk exports, user management.</div>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <label className="flex items-center gap-2 pt-2 cursor-pointer mt-4">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                            className="w-4 h-4 rounded text-indigo-600 bg-slate-900 border-slate-700"
                                        />
                                        <span className="text-sm text-slate-300">Set as Active immediately</span>
                                    </label>

                                    <div className="flex gap-3 pt-6 mt-2 border-t border-slate-700">
                                        <button
                                            type="button"
                                            onClick={handleCloseForm}
                                            className="flex-1 px-4 py-2 border border-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors font-medium"
                                        >
                                            Batal
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSaving}
                                            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium flex justify-center items-center gap-2"
                                        >
                                            {isSaving ? (
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <CheckCircle2 size={18} />
                                            )}
                                            {isSaving ? 'Menyimpan...' : 'Simpan'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </DashboardLayout>
        </RequireAdmin>
    );
}
